import { ArrowRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, RoleGate } from '@/shared/ui';

import type { FulfilmentPatchTarget } from '../lib/orderFulfilmentStatusTransitions';
import { getAllowedFulfilmentStatusTargets } from '../lib/orderFulfilmentStatusTransitions';
import type { OrderStatus } from '../types';
import { OrderUpdateFulfilmentStatusDialog } from './OrderUpdateFulfilmentStatusDialog';

const ACTION_KEY: Record<FulfilmentPatchTarget, string> = {
  PICKING: 'detail.fulfilment.actions.markPicking',
  PARTIALLY_FULFILLED: 'detail.fulfilment.actions.markPartiallyFulfilled',
  PACKED: 'detail.fulfilment.actions.markPacked',
  DISPATCHED: 'detail.fulfilment.actions.markDispatched',
  DELIVERED: 'detail.fulfilment.actions.markDelivered',
};

export interface OrderFulfilmentStatusActionsProps {
  orderId: string;
  orderNumber: string;
  status: OrderStatus;
}

export function OrderFulfilmentStatusActions({
  orderId,
  orderNumber,
  status,
}: OrderFulfilmentStatusActionsProps) {
  const { t } = useTranslation('orders');
  const targets = useMemo(() => getAllowedFulfilmentStatusTargets(status), [status]);
  const [pendingTarget, setPendingTarget] = useState<FulfilmentPatchTarget | null>(null);

  if (targets.length === 0) return null;

  return (
    <RoleGate allow={['admin']}>
      <div className="flex flex-wrap gap-2">
        {targets.map((target) => (
          <Button
            key={target}
            type="button"
            variant={target === 'DELIVERED' || target === 'PICKING' ? 'primary' : 'secondary'}
            className="gap-2 shadow-sm"
            onClick={() => setPendingTarget(target)}
          >
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
            {t(ACTION_KEY[target] as 'detail.fulfilment.actions.markPacked')}
          </Button>
        ))}
      </div>

      <OrderUpdateFulfilmentStatusDialog
        open={Boolean(pendingTarget)}
        orderId={orderId}
        orderNumber={orderNumber}
        currentStatus={status}
        targetStatus={pendingTarget}
        onOpenChange={(open) => {
          if (!open) setPendingTarget(null);
        }}
      />
    </RoleGate>
  );
}
